import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Tooltip,
  Snackbar,
  Checkbox,
  TablePagination,
  MenuItem
} from '@mui/material';
import {
  People as PeopleIcon,
  Assignment as AssignmentIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Block as BlockIcon,
  CheckCircle as CheckCircleIcon,
  CloudDownload as DownloadIcon,
  InsertDriveFile as FileIcon,
  GetApp as ExcelIcon,
  Info as InfoIcon,
  Link as LinkIcon,
  PersonAdd as PersonAddIcon,
  PlayArrow as PlayArrowIcon,
  BarChart as BarChartIcon,
  UploadFile as UploadFileIcon
} from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useNavigate } from 'react-router-dom';
import SurveyStats from '../../components/SurveyStats';
import InterviewersDialog from '../../components/InterviewersDialog';
import GroupIcon from '@mui/icons-material/Group';
import { usersToExcelRows } from '../../utils/userExport';
import { describeQuestionTargets } from '../../utils/interviewerTargets';
import { downloadSurveySav } from '../../utils/surveySavExport';

// Функция перевода категорий
const translateCategory = (category) => {
  const translations = {
    'online-surveys': 'Онлайн-опросы',
    'online-interviews': 'Онлайн-интервью и фокус-группы',
    'website-app-testing': 'Тест сайтов и приложений',
    'store-visits': 'Визиты в магазины и офисы',
    'product-testing': 'Тестирование продуктов и услуг',
    'other': 'Другое',
    // Старые категории для обратной совместимости
    'general': 'Онлайн-опросы',
    'market': 'Онлайн-опросы',
    'market-research': 'Онлайн-опросы',
  };
  return translations[category] || category;
};

// Функция для перевода статусов модерации ответов
const getResponseStatusLabel = (status) => {
  const statusMap = {
    'pending': 'Ожидает',
    'completed': 'Завершён',
    'approved': 'Одобрен',
    'rejected': 'Отклонён',
    'under_review': 'На проверке',
    'disqualified': 'Дисквалифицирован'
  };
  return statusMap[status] || status;
};

// Функция для выбора цвета статуса ответа
const getResponseStatusColor = (status) => {
  const colorMap = {
    'approved': 'success',
    'completed': 'success',
    'rejected': 'error',
    'under_review': 'warning',
    'pending': 'default'
  };
  return colorMap[status] || 'default';
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [currentTab, setCurrentTab] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalSurveys: 0,
    activeSurveys: 0,
    totalRevenue: 0
  });
  const [users, setUsers] = useState([]);
  const [surveys, setSurveys] = useState([]);
  const [profileSurveys, setProfileSurveys] = useState([]);
  const [openTestAccountDialog, setOpenTestAccountDialog] = useState(false);
  const [testAccountData, setTestAccountData] = useState({
    email: '',
    fullName: '',
    phone: '',
    password: '',
    role: 'respondent'
  });
  const [testAccountError, setTestAccountError] = useState('');
  const [testAccountSuccess, setTestAccountSuccess] = useState('');
  const [creatingTestAccount, setCreatingTestAccount] = useState(false);
  const [completedSurveys, setCompletedSurveys] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [rewardClaims, setRewardClaims] = useState([]);
  // Claim status dialog state
  const [claimStatusDialogOpen, setClaimStatusDialogOpen] = useState(false);
  const [claimStatusTarget, setClaimStatusTarget] = useState(null); // claim object
  const [claimStatusNewStatus, setClaimStatusNewStatus] = useState('');
  const [claimTrackingNumber, setClaimTrackingNumber] = useState('');
  const [claimShippingComment, setClaimShippingComment] = useState('');
  const [claimRejectionComment, setClaimRejectionComment] = useState('');
  const [claimStatusSubmitting, setClaimStatusSubmitting] = useState(false);
  const [claimStatusError, setClaimStatusError] = useState('');
  const [responsesOpen, setResponsesOpen] = useState(false);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesError, setResponsesError] = useState('');
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [responses, setResponses] = useState([]);
  // Админские фильтры, выбор и массовое отклонение в окне «Ответы для: …»
  const [respFilterInterviewer, setRespFilterInterviewer] = useState('');
  const [respFilterStatus, setRespFilterStatus] = useState('');
  const [respSelected, setRespSelected] = useState([]);
  const [respPage, setRespPage] = useState(0);
  const [respRowsPerPage, setRespRowsPerPage] = useState(50);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPreview, setBulkPreview] = useState(null);
  const [bulkReason, setBulkReason] = useState('');
  const [bulkTypedCount, setBulkTypedCount] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkResult, setBulkResult] = useState(null);
  const [responseImportOpen, setResponseImportOpen] = useState(false);
  const [responseImportFile, setResponseImportFile] = useState(null);
  const [responseImportPreview, setResponseImportPreview] = useState(null);
  const [responseImportBusy, setResponseImportBusy] = useState(false);
  const [responseImportError, setResponseImportError] = useState('');
  const [responseImportResult, setResponseImportResult] = useState(null);
  const [responseDetailOpen, setResponseDetailOpen] = useState(false);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userProfileSurveys, setUserProfileSurveys] = useState([]);
  const [userProfileCompletion, setUserProfileCompletion] = useState(null);
  const [userProfileAnswers, setUserProfileAnswers] = useState([]);
  const [loadingProfileData, setLoadingProfileData] = useState(false);
  
  // Users cache for displaying names instead of IDs
  const [usersCache, setUsersCache] = useState({});
  
  // Link copied snackbar
  const [linkCopied, setLinkCopied] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  
  // User edit dialog state
  const [userEditDialogOpen, setUserEditDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    email: '',
    fullName: '',
    phone: '',
    balance: 0,
    points: 0
  });
  const [savingUserEdit, setSavingUserEdit] = useState(false);
  const [userEditError, setUserEditError] = useState('');
  const [userEditSuccess, setUserEditSuccess] = useState('');
  
  const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
  const [editingReward, setEditingReward] = useState(null);
  const [newReward, setNewReward] = useState({ title: '', description: '', points: 0, image: '', stock: 0, active: true });
  const [rewardImageFile, setRewardImageFile] = useState(null);
  const [rewardImagePreview, setRewardImagePreview] = useState('');
  const [creatingReward, setCreatingReward] = useState(false);
  const [userPage, setUserPage] = useState(1);
  const [surveyPage, setSurveyPage] = useState(1);
  const [userPagination, setUserPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [surveyPagination, setSurveyPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [userSortBy, setUserSortBy] = useState('createdAt'); // 'createdAt', 'points', 'money'
  const [userSortOrder, setUserSortOrder] = useState('desc'); // 'asc' or 'desc'
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [selectedSurveyForStats, setSelectedSurveyForStats] = useState(null);
  const [statsResponses, setStatsResponses] = useState([]);
  const [exportingUsers, setExportingUsers] = useState(false);
  const [usersExportError, setUsersExportError] = useState('');

  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
  const SERVER_BASE = API_BASE.includes('/api') ? API_BASE.substring(0, API_BASE.indexOf('/api')) : API_BASE;

  useEffect(() => {
    fetchDashboardData();
  }, [currentTab, searchTerm, userPage, surveyPage, userSortBy, userSortOrder]);

  // Функция для скачивания файла
  // Load profile survey data for selected user (admin view)
  const loadUserProfileSurveyData = async (user) => {
    if (!user || !user.id) return;
    try {
      setLoadingProfileData(true);
      const token = localStorage.getItem('token');
      const [completionRes, surveysRes, answersRes] = await Promise.all([
        axios.get(`${API_BASE}/surveys/admin/users/${user.id}/profile/completion`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => null),
        axios.get(`${API_BASE}/surveys?profileSurveysOnly=true&status=active`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => null),
        axios.get(`${API_BASE}/surveys/admin/users/${user.id}/profile/survey-answers`, { headers: { Authorization: `Bearer ${token}` }}).catch(() => null)
      ]);
      
      if (completionRes?.data) setUserProfileCompletion(completionRes.data); else setUserProfileCompletion(null);
      const list = surveysRes?.data?.surveys || surveysRes?.data || [];
      setUserProfileSurveys(Array.isArray(list) ? list : []);
      setUserProfileAnswers(answersRes?.data || []);
    } catch (e) {
      console.error('Failed to load user profile survey data', e);
      setUserProfileCompletion(null);
      setUserProfileSurveys([]);
      setUserProfileAnswers([]);
    } finally {
      setLoadingProfileData(false);
    }
  };

  const handleDownloadFile = (filePath) => {
    const fileUrl = filePath.startsWith('/') ? `${SERVER_BASE}${filePath}` : filePath;
    window.open(fileUrl, '_blank');
  };

  // Функция экспорта ответов в Excel
  const handleExportResponsesToExcel = (list = responses, suffix = 'filtered') => {
    if (!selectedSurvey || !list.length) return;

    const questions = selectedSurvey.questions || [];
    const headers = ['№', 'Респондент', 'Данные респондента', 'Дата', 'Время (сек)', 'Статус'];
    questions.forEach((question) => headers.push(question.text || ''));
    (selectedSurvey.interviewerQuestions || []).forEach((iq) => {
      const base = `Доп. вопрос: ${iq.text}${iq.archived ? ' (архив)' : ''} (${describeQuestionTargets(iq, selectedSurvey.interviewers)})`;
      let header = base;
      let n = 2;
      while (headers.includes(header)) { header = `${base} (${n})`; n += 1; }
      headers.push(header);
    });

    const exportData = list.map((response, idx) => {
      const user = response.user || {};
      const isAnonymous = !response.userId;
      const userName = isAnonymous ? 'Анонимно' : (user.fullName || user.email || `Респондент ${idx + 1}`);
      const userInfo = isAnonymous ? 'Анонимный ответ' : [
        user.fullName || '',
        user.email || '',
        user.age ? `${user.age} лет` : '',
        user.gender || '',
        user.city || '',
        user.region || ''
      ].filter(Boolean).join(', ');

      const values = [
        idx + 1,
        userName,
        userInfo || 'Н/Д',
        response.completedAt ? new Date(response.completedAt).toLocaleString() : 'Н/Д',
        response.timeSpent || 'Н/Д',
        getResponseStatusLabel(response.status || 'pending')
      ];

      questions.forEach((question, qIdx) => {
        const questionId = question.id || `q_${qIdx}`;
        const answer = response.answers?.[questionId];

        if (question.type === 'file') {
          if (typeof answer === 'object' && answer?.fileUrl) {
            values.push(`Файл: ${answer.originalName || 'файл загружен'}`);
          } else if (answer) {
            values.push('Файл загружен');
          } else {
            values.push('Нет файла');
          }
        } else if (question.type === 'ranking') {
          values.push(Array.isArray(answer) ? answer.join(' → ') : 'Нет ответа');
        } else if (question.type === 'matrix') {
          if (answer && typeof answer === 'object' && !Array.isArray(answer)) {
            const matrixStr = Object.entries(answer).map(([rowKey, colVal]) => `${rowKey}: ${colVal}`).join('; ');
            values.push(matrixStr || 'Нет ответа');
          } else {
            values.push('Нет ответа');
          }
        } else if (question.type === 'scale' || question.type === 'nps') {
          values.push(answer != null ? answer.toString() : 'Нет ответа');
        } else if (Array.isArray(answer)) {
          values.push(answer.join(', '));
        } else {
          values.push(answer || 'Нет ответа');
        }
      });

      (selectedSurvey.interviewerQuestions || []).forEach((iq) => {
        const ia = response.interviewerAnswers ? response.interviewerAnswers[iq.id] : undefined;
        const text = Array.isArray(ia) ? ia.join(', ') : (ia === undefined || ia === null ? '' : String(ia));
        values.push(/^[=+\-@\t\r]/.test(text) ? `'${text}` : text);
      });

      return values;
    });

    const ws = XLSX.utils.aoa_to_sheet([headers, ...exportData]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ответы');
    
    // Скачиваем файл
    XLSX.writeFile(wb, `survey_${selectedSurvey.id}_responses_${suffix}_${Date.now()}.xlsx`);
  };

  const handleExportResponsesToSav = (list = responses) => {
    if (!selectedSurvey || !list.length) return;
    downloadSurveySav(selectedSurvey, list, { showInterviewers: true });
  };

  const handleExportUsersToExcel = async () => {
    try {
      setExportingUsers(true);
      setUsersExportError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/users/export/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const allUsers = response.data?.users || [];
      if (!allUsers.length) {
        setUsersExportError('Нет пользователей для выгрузки');
        return;
      }

      const exportData = usersToExcelRows(allUsers);
      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Пользователи');
      XLSX.writeFile(wb, `thinklink_users_${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Error exporting users:', error);
      setUsersExportError(error.response?.data?.error || 'Не удалось выгрузить пользователей');
    } finally {
      setExportingUsers(false);
    }
  };

  // Заполнить кэш пользователей из response.user, не перезаписывая уже загруженные полные записи
  const seedUsersCacheFromResponses = (list) => {
    const fromResponses = {};
    (list || []).forEach((rw) => {
      if (rw && rw.userId && rw.user && typeof rw.user === 'object') fromResponses[rw.userId] = rw.user;
    });
    if (Object.keys(fromResponses).length === 0) return;
    setUsersCache(prev => ({ ...fromResponses, ...prev }));
  };

  const fetchUserInfo = async (userId) => {
    if (usersCache[userId]) {
      return usersCache[userId];
    }
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const user = response.data;
      setUsersCache(prev => ({ ...prev, [userId]: user }));
      return user;
    } catch (err) {
      console.error('Error fetching user:', err);
      return null;
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const userStatsRes = await axios.get(`${API_BASE}/users/stats/overview`, config);
      // Fetch high-level surveys and finance stats immediately (not tied to current tab)
      const [surveyStatsRes, financeOverviewRes] = await Promise.all([
        axios.get(`${API_BASE}/surveys/stats/overview`, config),
        axios.get(`${API_BASE}/rewards/admin/overview`, config)
      ]);
      
      // Fetch users with pagination and search
      const userParams = new URLSearchParams({
        page: userPage.toString(),
        limit: '20',
        sortBy: userSortBy,
        sortOrder: userSortOrder
      });
      if (currentTab === 0 && searchTerm) {
        userParams.append('search', searchTerm);
      }
      const usersRes = await axios.get(`${API_BASE}/users?${userParams.toString()}`, config);
      if (usersRes.data.users) {
        setUsers(usersRes.data.users);
        setUserPagination(usersRes.data.pagination || { page: 1, limit: 20, total: usersRes.data.users.length, totalPages: 1 });
      } else {
        setUsers(usersRes.data);
        setUserPagination({ page: 1, limit: 20, total: usersRes.data.length, totalPages: 1 });
      }

      // Fetch surveys with pagination and search (exclude profile surveys)
      const surveyParams = new URLSearchParams({
        page: surveyPage.toString(),
        limit: '20',
        excludeProfileSurveys: 'true'
      });
      if (currentTab === 1 && searchTerm) {
        surveyParams.append('search', searchTerm);
      }
      const surveysRes = await axios.get(`${API_BASE}/surveys?${surveyParams.toString()}`, config);
      if (surveysRes.data.surveys) {
        const surveysArray = surveysRes.data.surveys;
        setSurveys(surveysArray);
        setSurveyPagination(surveysRes.data.pagination || { page: 1, limit: 20, total: surveysArray.length, totalPages: 1 });
        
        // Load user info for surveys
        const surveyUserIds = [...new Set(surveysArray.map(s => s.clientId).filter(Boolean))];
        await Promise.all(surveyUserIds.map(id => fetchUserInfo(id)));
      } else {
        const surveysArray = surveysRes.data;
        setSurveys(surveysArray);
        setSurveyPagination({ page: 1, limit: 20, total: surveysArray.length, totalPages: 1 });
        
        // Load user info for surveys
        const surveyUserIds = [...new Set(surveysArray.map(s => s.clientId).filter(Boolean))];
        await Promise.all(surveyUserIds.map(id => fetchUserInfo(id)));
      }

      // Fetch profile surveys separately
      const profileSurveysRes = await axios.get(`${API_BASE}/surveys?profileSurveysOnly=true`, config);
      const profileSurveysData = profileSurveysRes.data.surveys || profileSurveysRes.data || [];
      const profileSurveysArray = Array.isArray(profileSurveysData) ? profileSurveysData : [];
      setProfileSurveys(profileSurveysArray);

      // Admin datasets
      const [wdRes, rwRes, allWithdrawalsRes, claimsRes] = await Promise.all([
        axios.get(`${API_BASE}/rewards/admin/withdrawals?status=pending`, config),
        axios.get(`${API_BASE}/rewards/admin/rewards`, config),
        axios.get(`${API_BASE}/rewards/admin/withdrawals`, config),
        axios.get(`${API_BASE}/rewards/admin/claims`, config)
      ]);
      setWithdrawals(wdRes.data || []);
      setRewards(rwRes.data || []);
      setRewardClaims(claimsRes.data || []);
      
      // Fetch completed surveys
      const completedSurveysRes = await axios.get(`${API_BASE}/surveys?status=completed`, config);
      const completedSurveysData = completedSurveysRes.data.surveys || completedSurveysRes.data || [];
      const completedSurveysArray = Array.isArray(completedSurveysData) ? completedSurveysData : [];
      
      // Load user info for completed surveys
      const completedUserIds = [...new Set(completedSurveysArray.map(s => s.clientId).filter(Boolean))];
      await Promise.all(completedUserIds.map(id => fetchUserInfo(id)));
      
      setCompletedSurveys(completedSurveysArray);
      
      // Считаем сумму одобренных выплат
      const approvedWithdrawals = (allWithdrawalsRes.data || []).filter(w => w.status === 'approved');
      const totalRevenue = approvedWithdrawals.reduce((sum, w) => sum + (w.amount || 0), 0);

      setStats(prev => ({
        totalUsers: userStatsRes.data.total,
        totalSurveys: surveyStatsRes?.data?.total ?? (surveysRes.data.pagination?.total || surveysRes.data.length || 0),
        activeSurveys: surveyStatsRes?.data?.active ?? (surveysRes.data.surveys ? surveysRes.data.surveys.filter(s => s.status === 'active').length : 0),
        totalRevenue: totalRevenue,
        moneyAvailable: financeOverviewRes?.data?.totalMoneyAvailable ?? prev.moneyAvailable ?? 0,
        pointsAvailable: financeOverviewRes?.data?.totalPointsAvailable ?? prev.pointsAvailable ?? 0
      }));
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting user:', error);
    }
  };

  const handleDeleteSurvey = async (surveyId) => {
    if (!window.confirm('Are you sure you want to delete this survey?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/surveys/${surveyId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting survey:', error);
    }
  };

  const handleExportSurvey = async (surveyId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_BASE}/surveys/${surveyId}/export.csv`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      // Trigger download
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `survey_${surveyId}_responses.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting survey responses:', error);
    }
  };

  const [interviewersSurvey, setInterviewersSurvey] = useState(null);

  const responsesFilterParams = (interviewerId = respFilterInterviewer, status = respFilterStatus) => {
    const params = {};
    if (interviewerId) params.interviewerId = interviewerId;
    if (status) params.status = status;
    return params;
  };

  const openResponses = async (survey, filters) => {
    try {
      const isNewSurvey = !selectedSurvey || selectedSurvey.id !== survey.id;
      let interviewerId = respFilterInterviewer;
      let status = respFilterStatus;
      if (filters) {
        interviewerId = filters.interviewerId ?? '';
        status = filters.status ?? '';
      } else if (isNewSurvey) {
        interviewerId = '';
        status = '';
      }
      setRespFilterInterviewer(interviewerId);
      setRespFilterStatus(status);
      setRespSelected([]);
      setRespPage(0);
      setSelectedSurvey(survey);
      setResponsesOpen(true);
      setResponsesLoading(true);
      setResponsesError('');
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_BASE}/surveys/${survey.id}/responses`, {
        headers: { Authorization: `Bearer ${token}` },
        params: responsesFilterParams(interviewerId, status)
      });
      const responsesData = Array.isArray(data) ? data : [];
      setResponses(responsesData);
      
      // Данные пользователя уже приходят в response.user (тот же запрос) — кладём их в кэш
      // вместо отдельного GET /users/:id на каждого респондента. Отдельно запрашиваем только тех,
      // для кого сервер не вернул user (раньше такие запросы тоже шли и обычно давали 404).
      seedUsersCacheFromResponses(responsesData);
      const missingUserIds = [...new Set(responsesData.filter(r => r.userId && !r.user).map(r => r.userId))];
      await Promise.all(missingUserIds.map(id => fetchUserInfo(id)));
    } catch (e) {
      console.error('Failed to load responses', e);
      setResponsesError('Failed to load responses');
    } finally {
      setResponsesLoading(false);
    }
  };

  const openStats = async (survey) => {
    try {
      setSelectedSurveyForStats(survey);
      setStatsDialogOpen(true);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_BASE}/surveys/${survey.id}/responses`, { headers: { Authorization: `Bearer ${token}` } });
      const responsesData = Array.isArray(data) ? data : [];
      setStatsResponses(responsesData);
      // SurveyStats берёт данные пользователя из response.user — отдельные запросы не нужны
      seedUsersCacheFromResponses(responsesData);
    } catch (e) {
      console.error('Failed to load stats', e);
      setStatsResponses([]);
    }
  };

  const closeStats = () => {
    setStatsDialogOpen(false);
    setSelectedSurveyForStats(null);
    setStatsResponses([]);
  };

  const rejectResponse = async (responseId) => {
    if (!selectedSurvey) return;
    const reason = window.prompt('Укажите причину отклонения ответа (необязательно):', '');
    if (!window.confirm('Отклонить этот ответ и отменить вознаграждение?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API_BASE}/surveys/${selectedSurvey.id}/responses/${responseId}/reject`,
        { reason: reason || '' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // refresh responses and surveys
      await openResponses(selectedSurvey);
      await fetchDashboardData();
    } catch (e) {
      console.error('Failed to reject response', e);
      alert(e.response?.data?.error || 'Failed to reject response');
    }
  };

  const exportAllResponsesToExcel = async () => {
    if (!selectedSurvey) return;
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_BASE}/surveys/${selectedSurvey.id}/responses`, { headers: { Authorization: `Bearer ${token}` } });
      handleExportResponsesToExcel(Array.isArray(data) ? data : [], 'all');
    } catch (e) {
      console.error('Failed to export all responses', e);
      alert('Не удалось выгрузить все ответы');
    }
  };

  const responseImportFormData = (file, dryRun, expectedRows, previewToken) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dryRun', String(dryRun));
    if (expectedRows !== undefined) formData.append('expectedRows', String(expectedRows));
    if (previewToken) formData.append('previewToken', previewToken);
    return formData;
  };

  const formatResponseImportError = (error) => {
    const data = error.response?.data;
    const message = data?.error || error.message || 'Не удалось проверить Excel-файл';
    const details = Array.isArray(data?.details) ? data.details.slice(0, 8) : [];
    if (details.length === 0) return message;
    const lines = details.map((item) => {
      const position = item.row ? `Строка ${item.row}${item.column ? `, колонка ${item.column}` : ''}: ` : '';
      return `${position}${item.error || 'ошибка'}`;
    });
    const rest = data.details.length > details.length ? `\n…и ещё ${data.details.length - details.length}` : '';
    return `${message}\n${lines.join('\n')}${rest}`;
  };

  const previewResponseImport = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file || !selectedSurvey) return;

    setResponseImportOpen(true);
    setResponseImportFile(file);
    setResponseImportPreview(null);
    setResponseImportResult(null);
    setResponseImportError('');

    if (!file.name.toLowerCase().endsWith('.xlsx')) {
      setResponseImportError('Поддерживаются только файлы .xlsx');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setResponseImportError('Excel-файл не должен превышать 10 МБ');
      return;
    }

    setResponseImportBusy(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_BASE}/surveys/${selectedSurvey.id}/responses/import`,
        responseImportFormData(file, true),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResponseImportPreview(data);
    } catch (error) {
      console.error('Failed to preview response import', error);
      setResponseImportError(formatResponseImportError(error));
    } finally {
      setResponseImportBusy(false);
    }
  };

  const confirmResponseImport = async () => {
    if (!selectedSurvey || !responseImportFile || !responseImportPreview) return;
    setResponseImportBusy(true);
    setResponseImportError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API_BASE}/surveys/${selectedSurvey.id}/responses/import`,
        responseImportFormData(
          responseImportFile,
          false,
          responseImportPreview.willApply,
          responseImportPreview.previewToken
        ),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResponseImportResult(data);
      setResponseImportPreview(null);
      await openResponses(selectedSurvey, {
        interviewerId: respFilterInterviewer,
        status: respFilterStatus
      });
      await fetchDashboardData();
    } catch (error) {
      console.error('Failed to import responses', error);
      if (error.response?.status === 409 && Number.isInteger(error.response?.data?.willApply)) {
        setResponseImportPreview(error.response.data);
      }
      setResponseImportError(formatResponseImportError(error));
    } finally {
      setResponseImportBusy(false);
    }
  };

  const closeResponseImport = () => {
    if (responseImportBusy) return;
    setResponseImportOpen(false);
    setResponseImportFile(null);
    setResponseImportPreview(null);
    setResponseImportError('');
    setResponseImportResult(null);
  };

  const respInterviewerOptions = (() => {
    const map = new Map();
    (selectedSurvey?.interviewers || []).forEach((i) => { if (i && i.userId) map.set(i.userId, i.name || i.userId); });
    responses.forEach((r) => { if (r.interviewerId && !map.has(r.interviewerId)) map.set(r.interviewerId, r.interviewerName || r.interviewerId); });
    return [...map.entries()].map(([id, name]) => ({ id, name })).sort((a, b) => String(a.name).localeCompare(String(b.name), 'ru'));
  })();
  const respSelectableIds = responses.filter((r) => r.status !== 'rejected').map((r) => r.id);
  const respPageRows = responses.slice(respPage * respRowsPerPage, respPage * respRowsPerPage + respRowsPerPage);
  const respPageSelectable = respPageRows.filter((r) => r.status !== 'rejected').map((r) => r.id);
  const respPageAllSelected = respPageSelectable.length > 0 && respPageSelectable.every((id) => respSelected.includes(id));

  const toggleRespSelected = (id) => {
    setRespSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  const toggleRespPageSelected = () => {
    setRespSelected((prev) => (respPageAllSelected
      ? prev.filter((id) => !respPageSelectable.includes(id))
      : [...new Set([...prev, ...respPageSelectable])]));
  };

  const openBulkReject = async () => {
    if (!selectedSurvey || respSelected.length === 0) return;
    setBulkOpen(true);
    setBulkPreview(null);
    setBulkReason('');
    setBulkTypedCount('');
    setBulkError('');
    setBulkResult(null);
    setBulkBusy(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_BASE}/surveys/${selectedSurvey.id}/responses/bulk-reject`,
        { responseIds: respSelected, dryRun: true },
        { headers: { Authorization: `Bearer ${token}` } });
      setBulkPreview(data);
    } catch (e) {
      setBulkError(e.response?.data?.error || 'Не удалось подготовить отклонение');
    } finally {
      setBulkBusy(false);
    }
  };

  const confirmBulkReject = async () => {
    if (!selectedSurvey || !bulkPreview) return;
    setBulkBusy(true);
    setBulkError('');
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_BASE}/surveys/${selectedSurvey.id}/responses/bulk-reject`,
        { responseIds: respSelected, reason: bulkReason, expectedCount: bulkPreview.count },
        { headers: { Authorization: `Bearer ${token}` } });
      setBulkResult(data);
      await openResponses(selectedSurvey, { interviewerId: respFilterInterviewer, status: respFilterStatus });
      await fetchDashboardData();
    } catch (e) {
      setBulkError(e.response?.data?.error || 'Не удалось отклонить ответы');
    } finally {
      setBulkBusy(false);
    }
  };

  const approveResponse = async (responseId) => {
    if (!selectedSurvey) return;
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/surveys/${selectedSurvey.id}/responses/${responseId}/approve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await openResponses(selectedSurvey);
      await fetchDashboardData();
    } catch (e) {
      console.error('Failed to approve response', e);
      alert('Failed to approve response');
    }
  };

  const handleApproveWithdrawal = async (id) => {
    const screenshotUrl = prompt('Введите ссылку на скриншот подтверждения перевода (необязательно):');
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/rewards/admin/withdrawals/${id}/approve`, 
        { screenshotUrl: screenshotUrl || undefined }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDashboardData();
    } catch (error) {
      console.error('Error approving withdrawal:', error);
      alert('Ошибка при одобрении вывода средств');
    }
  };

  const handleRejectWithdrawal = async (id) => {
    const rejectionComment = prompt('Укажите причину отклонения вывода средств:');
    if (!rejectionComment) {
      alert('Необходимо указать причину отклонения');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/rewards/admin/withdrawals/${id}/reject`, 
        { rejectionComment }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchDashboardData();
    } catch (error) {
      console.error('Error rejecting withdrawal:', error);
      alert('Ошибка при отклонении вывода средств');
    }
  };

  const openClaimStatusDialog = (claim, status) => {
    setClaimStatusTarget(claim);
    setClaimStatusNewStatus(status);
    setClaimTrackingNumber('');
    setClaimShippingComment('');
    setClaimRejectionComment('');
    setClaimStatusError('');
    setClaimStatusDialogOpen(true);
  };

  const submitClaimStatus = async () => {
    if (!claimStatusTarget) return;
    setClaimStatusError('');
    // Validation
    if (claimStatusNewStatus === 'cancelled' && !claimRejectionComment.trim()) {
      setClaimStatusError('Причина отмены обязательна');
      return;
    }
    if (claimStatusNewStatus === 'shipped') {
      if (!claimTrackingNumber.trim()) {
        setClaimStatusError('Трек-номер обязателен');
        return;
      }
      if (!claimShippingComment.trim()) {
        setClaimStatusError('Комментарий обязателен');
        return;
      }
    }
    try {
      setClaimStatusSubmitting(true);
      const token = localStorage.getItem('token');
      const payload = { status: claimStatusNewStatus };
      if (claimStatusNewStatus === 'cancelled') {
        payload.rejectionComment = claimRejectionComment.trim();
      } else if (claimStatusNewStatus === 'shipped') {
        payload.trackingNumber = claimTrackingNumber.trim();
        payload.shippingComment = claimShippingComment.trim();
      }
      await axios.put(
        `${API_BASE}/rewards/admin/claims/${claimStatusTarget.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setClaimStatusDialogOpen(false);
      setClaimStatusTarget(null);
      await fetchDashboardData();
    } catch (error) {
      console.error('Error updating claim status:', error);
      setClaimStatusError(error.response?.data?.error || 'Ошибка при обновлении статуса');
    } finally {
      setClaimStatusSubmitting(false);
    }
  };

  const handleOpenRewardDialog = (reward = null) => {
    if (reward) {
      // Режим редактирования
      setEditingReward(reward);
      setNewReward({
        title: reward.title || '',
        description: reward.description || '',
        points: reward.points || 0,
        image: reward.image || '',
        stock: reward.stock || 0,
        active: reward.active !== false
      });
      // Установить превью текущего изображения
      if (reward.image) {
        const imageSrc = reward.image.startsWith('/uploads')
          ? `${SERVER_BASE}${reward.image}`
          : reward.image;
        setRewardImagePreview(imageSrc);
      } else {
        setRewardImagePreview('');
      }
    } else {
      // Режим создания
      setEditingReward(null);
      setNewReward({ title: '', description: '', points: 0, image: '', stock: 0, active: true });
      setRewardImagePreview('');
    }
    setRewardImageFile(null);
    setRewardDialogOpen(true);
  };

  const handleCloseRewardDialog = () => {
    setRewardDialogOpen(false);
    setEditingReward(null);
    setNewReward({ title: '', description: '', points: 0, image: '', stock: 0, active: true });
    setRewardImageFile(null);
    setRewardImagePreview('');
  };

  const handleRewardImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setRewardImageFile(file);
      // Создаём превью
      const reader = new FileReader();
      reader.onloadend = () => {
        setRewardImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveReward = async () => {
    try {
      setCreatingReward(true);
      console.log(editingReward ? 'Updating reward...' : 'Creating reward...', { newReward, hasFile: !!rewardImageFile });
      
      // Валидация
      if (!newReward.title || !newReward.description) {
        alert('Пожалуйста, заполните название и описание');
        setCreatingReward(false);
        return;
      }
      
      if (newReward.points <= 0) {
        alert('Укажите количество баллов больше 0');
        setCreatingReward(false);
        return;
      }
      
      const token = localStorage.getItem('token');
      
      let imageUrl = newReward.image || '';
      
      // Если выбран файл, сначала загружаем его
      if (rewardImageFile) {
        console.log('Uploading image file:', rewardImageFile.name);
        const formData = new FormData();
        formData.append('image', rewardImageFile);
        
        const uploadRes = await axios.post(`${API_BASE}/upload/reward-image`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        
        console.log('Upload response:', uploadRes.data);
        imageUrl = uploadRes.data.imageUrl;
      }
      
      const rewardData = {
        title: newReward.title,
        description: newReward.description,
        points: parseInt(newReward.points) || 0,
        image: imageUrl,
        stock: parseInt(newReward.stock) || 0,
        active: newReward.active
      };
      
      console.log(editingReward ? 'Updating' : 'Creating', 'reward with data:', rewardData);
      
      if (editingReward) {
        // Обновление существующей награды
        await axios.put(`${API_BASE}/rewards/admin/rewards/${editingReward.id}`, rewardData, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
      } else {
        // Создание новой награды
        await axios.post(`${API_BASE}/rewards/admin/rewards`, rewardData, { 
          headers: { Authorization: `Bearer ${token}` } 
        });
      }
      
      console.log('Reward saved successfully');
      
      handleCloseRewardDialog();
      fetchDashboardData();
    } catch (error) {
      console.error('Error saving reward:', error);
      console.error('Error response:', error.response?.data);
      alert(`Ошибка при ${editingReward ? 'обновлении' : 'создании'} подарка: ` + (error.response?.data?.error || error.message));
    } finally {
      setCreatingReward(false);
    }
  };

  const handleDeleteReward = async (id) => {
    if (!window.confirm('Удалить этот подарок?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_BASE}/rewards/admin/rewards/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchDashboardData();
    } catch (error) {
      console.error('Error deleting reward:', error);
    }
  };

  // User edit handlers
  const handleOpenUserEdit = (user) => {
    setEditingUser(user);
    setEditUserData({
      email: user.email || '',
      fullName: user.fullName || '',
      phone: user.phone || '',
      balance: user.balance || 0,
      points: user.points || 0
    });
    setUserEditError('');
    setUserEditSuccess('');
    setUserEditDialogOpen(true);
  };

  const handleCloseUserEdit = () => {
    setUserEditDialogOpen(false);
    setEditingUser(null);
    setEditUserData({ email: '', fullName: '', phone: '', balance: 0, points: 0 });
    setUserEditError('');
    setUserEditSuccess('');
  };

  const handleSaveUserEdit = async () => {
    if (!editingUser) return;
    
    setSavingUserEdit(true);
    setUserEditError('');
    setUserEditSuccess('');
    
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE}/users/${editingUser.id}`, {
        email: editUserData.email,
        fullName: editUserData.fullName,
        phone: editUserData.phone,
        balance: parseFloat(editUserData.balance) || 0,
        points: parseFloat(editUserData.points) || 0
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setUserEditSuccess('Данные пользователя успешно обновлены');
      fetchDashboardData();
      
      // Close dialog after short delay
      setTimeout(() => {
        handleCloseUserEdit();
      }, 1500);
    } catch (error) {
      console.error('Error updating user:', error);
      setUserEditError(error.response?.data?.error || 'Не удалось обновить данные пользователя');
    } finally {
      setSavingUserEdit(false);
    }
  };

  const handleSearch = () => {
    setSearchTerm(searchInput);
    if (currentTab === 0) {
      setUserPage(1);
    } else {
      setSurveyPage(1);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSortUsers = (field) => {
    if (userSortBy === field) {
      setUserSortOrder(userSortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setUserSortBy(field);
      setUserSortOrder('desc');
    }
    setUserPage(1);
  };

  // No client-side filtering needed anymore - done on server
  const filteredUsers = users;
  const filteredSurveys = surveys;

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography 
          variant="h4" 
          sx={{
            fontWeight: 700,
            color: '#2d3748',
            fontSize: { xs: '1.75rem', md: '2.125rem' }
          }}
        >
          Панель администратора
        </Typography>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(9, 132, 227, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(9, 132, 227, 0.4)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>Всего пользователей</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.totalUsers}</Typography>
                </Box>
                <PeopleIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(86, 171, 47, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(86, 171, 47, 0.4)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>Всего опросов</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.totalSurveys}</Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(253, 203, 110, 0.3)',
              color: '#2d3748',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(253, 203, 110, 0.4)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ color: '#2d3748', fontWeight: 600, fontSize: '0.875rem' }}>Активные опросы</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.activeSurveys}</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(214, 48, 49, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(214, 48, 49, 0.4)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>Выплачено вознаграждений</Typography>
                  <Typography variant="h4" sx={{ fontWeight: 700, mt: 1 }}>{stats.totalRevenue.toFixed(2)} BYN</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)',
              backdropFilter: 'blur(20px)',
              borderRadius: '24px',
              border: '1px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 8px 32px rgba(108, 92, 231, 0.3)',
              color: '#fff',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 40px rgba(108, 92, 231, 0.4)'
              }
            }}
          >
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600, fontSize: '0.875rem' }}>К выплате респондентам</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>{(stats.moneyAvailable || 0).toFixed(2)} BYN</Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)', mt: 0.5 }}>{Number(stats.pointsAvailable || 0).toFixed(1)} баллов</Typography>
                </Box>
                <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.3 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper 
        sx={{ 
          mb: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '24px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'hidden'
        }}
      >
        <Tabs 
          value={currentTab} 
          onChange={(e, val) => {
            setCurrentTab(val);
            // Clear search only when switching away from searchable tabs
            if (val !== 0 && val !== 1) {
              setSearchTerm('');
              setSearchInput('');
            }
          }}
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '0.95rem',
              color: '#718096',
              transition: 'all 0.3s ease',
              '&.Mui-selected': {
                color: '#0984e3',
                fontWeight: 700
              },
              '&:hover': {
                color: '#0984e3',
                backgroundColor: 'rgba(116, 185, 255, 0.1)'
              }
            },
            '& .MuiTabs-indicator': {
              background: 'linear-gradient(90deg, #74b9ff 0%, #0984e3 100%)',
              height: 3,
              borderRadius: '3px 3px 0 0'
            }
          }}
        >
          <Tab label="Пользователи" />
          <Tab label="Опросы" />
          <Tab label="Профильные опросы" />
          <Tab label={`Завершенные опросы (${completedSurveys.length})`} />
          <Tab label="Выплаты" />
          <Tab label="Подарки за баллы" />
          <Tab label="Заявки на подарки" />
        </Tabs>
      </Paper>

      {(currentTab === 0 || currentTab === 1) && (
        <TextField
          fullWidth
          placeholder={currentTab === 0 ? "Поиск пользователей..." : "Поиск опросов..."}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <IconButton onClick={handleSearch}>
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}

      {currentTab === 0 && usersExportError && (
        <Alert severity="error" onClose={() => setUsersExportError('')} sx={{ mb: 2, borderRadius: '12px' }}>
          {usersExportError}
        </Alert>
      )}

      {currentTab === 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={exportingUsers ? <CircularProgress size={16} color="inherit" /> : <ExcelIcon />}
            onClick={handleExportUsersToExcel}
            disabled={exportingUsers}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              borderColor: '#0984e3',
              color: '#0984e3',
              '&:hover': {
                borderColor: '#0652DD',
                backgroundColor: 'rgba(9, 132, 227, 0.08)',
              },
            }}
          >
            Выгрузка в Excel
          </Button>
        </Box>
      )}

      {currentTab === 0 && (
        <TableContainer 
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  '& th': {
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: 'none',
                    py: 2
                  }
                }}
              >
                <TableCell 
                  sx={{ cursor: 'pointer', userSelect: 'none' }} 
                  onClick={() => handleSortUsers('createdAt')}
                >
                  Пользователь {userSortBy === 'createdAt' && (userSortOrder === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Роль</TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', userSelect: 'none' }} 
                  onClick={() => handleSortUsers('points')}
                >
                  Баллы {userSortBy === 'points' && (userSortOrder === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell 
                  sx={{ cursor: 'pointer', userSelect: 'none' }} 
                  onClick={() => handleSortUsers('money')}
                >
                  Рубли {userSortBy === 'money' && (userSortOrder === 'asc' ? '↑' : '↓')}
                </TableCell>
                <TableCell>Заблокирован</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow 
                  key={user.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(116, 185, 255, 0.08)',
                      transition: 'background-color 0.3s ease'
                    },
                    '& td': {
                      borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                      py: 2
                    }
                  }}
                >
                  <TableCell>
                    <Box>
                      <Typography variant="body2" fontWeight="600">
                        {user.fullName || 'Н/Д'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '0.7rem' }}>
                        Регистрация: {new Date(user.createdAt).toLocaleDateString()}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={user.role}
                      onChange={async (e) => {
                        try {
                          const token = localStorage.getItem('token');
                          await axios.put(`${API_BASE}/users/${user.id}`, { role: e.target.value }, { headers: { Authorization: `Bearer ${token}` } });
                          fetchDashboardData();
                        } catch (err) { console.error('Failed to update role', err); }
                      }}
                      SelectProps={{ native: true }}
                      sx={{ minWidth: 120 }}
                    >
                      <option value="admin">Администратор</option>
                      <option value="client">Заказчик</option>
                      <option value="respondent">Респондент</option>
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={`${Number(user.points || 0).toFixed(1)} б.`}
                      sx={{
                        background: 'linear-gradient(135deg, #f9ca24 0%, #f0932b 100%)',
                        color: '#000',
                        fontWeight: 600,
                        borderRadius: '12px'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={`${(user.balance || 0).toFixed(2)} BYN`}
                      sx={{
                        background: (user.balance || 0) >= 10 
                          ? 'linear-gradient(135deg, #00b894 0%, #00a383 100%)'
                          : 'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                        color: (user.balance || 0) >= 10 ? '#fff' : '#2d3748',
                        fontWeight: 600,
                        borderRadius: '12px'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      size="small" 
                      label={user.suspended ? 'Да' : 'Нет'} 
                      sx={{
                        background: user.suspended ? 
                          'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' : 
                          'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                        color: user.suspended ? '#2d3748' : '#fff',
                        fontWeight: 600,
                        borderRadius: '12px'
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.phoneVerified || user.isPhoneVerified ? 'Подтверждён' : 'Не подтверждён'} 
                      size="small" 
                      icon={user.phoneVerified || user.isPhoneVerified ? <CheckCircleIcon /> : <BlockIcon />}
                      sx={{
                        background: user.phoneVerified || user.isPhoneVerified ? 
                          'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' : 
                          'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                        color: user.phoneVerified || user.isPhoneVerified ? '#fff' : '#2d3748',
                        fontWeight: 600,
                        borderRadius: '12px',
                        '& .MuiChip-icon': {
                          color: user.phoneVerified || user.isPhoneVerified ? '#fff' : '#2d3748'
                        }
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Редактировать">
                      <IconButton size="small" color="primary" onClick={() => handleOpenUserEdit(user)}>
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" color={user.suspended ? 'success' : 'warning'} onClick={async () => {
                      try {
                        const token = localStorage.getItem('token');
                        await axios.put(`${API_BASE}/users/${user.id}`, { suspended: !user.suspended }, { headers: { Authorization: `Bearer ${token}` } });
                        fetchDashboardData();
                      } catch (err) { console.error('Failed to toggle suspension', err); }
                    }}>{user.suspended ? 'Разблокировать' : 'Заблокировать'}</Button>
                    <IconButton size="small" onClick={() => { setSelectedUser(user); setUserDialogOpen(true); loadUserProfileSurveyData(user); }}>
                      <VisibilityIcon />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDeleteUser(user.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {userPagination.totalPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Button
                    disabled={userPage === 1}
                    onClick={() => setUserPage(userPage - 1)}
                    sx={{ mr: 1 }}
                  >
                    Предыдущая
                  </Button>
                  <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                    Страница {userPage} из {userPagination.totalPages}
                  </Typography>
                  <Button
                    disabled={userPage === userPagination.totalPages}
                    onClick={() => setUserPage(userPage + 1)}
                    sx={{ ml: 1 }}
                  >
                    Следующая
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" align="center" color="text.secondary">
                    Показано {users.length} из {userPagination.total} пользователей
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </TableContainer>
      )}

      {currentTab === 1 && (
        <TableContainer 
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  '& th': {
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: 'none',
                    py: 2
                  }
                }}
              >
                <TableCell>Название</TableCell>
                <TableCell>Категория</TableCell>
                <TableCell>Заказчик</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Вопросы</TableCell>
                <TableCell>Ответы</TableCell>
                <TableCell>Награда</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell>Опубликован</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredSurveys.map((survey) => (
                <TableRow key={survey.id} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(116, 185, 255, 0.08)',
                    transition: 'background-color 0.3s ease'
                  }
                }}>
                  <TableCell>
                    {survey.title}
                    {survey.isPublicLink && (
                      <Chip 
                        label="Публичная ссылка" 
                        size="small" 
                        sx={{ 
                          ml: 1,
                          background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    )}
                    {survey.isExternalSurvey && (
                      <Chip 
                        label="Внешний опрос" 
                        size="small" 
                        sx={{ 
                          ml: 1,
                          background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{translateCategory(survey.category) || 'Н/Д'}</TableCell>
                  <TableCell>
                    {usersCache[survey.clientId]?.fullName || survey.clientId || 'Неизвестно'}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        survey.status === 'active' ? 'Активен' : 
                        survey.status === 'draft' ? 'Черновик' : 
                        survey.status === 'pending' ? 'На модерации' : 
                        survey.status === 'pending_moderation' ? 'На модерации' : 
                        survey.status === 'rejected' ? 'Отклонён' : 
                        survey.status === 'completed' ? 'Завершён' : 
                        survey.status
                      } 
                      size="small" 
                      sx={{
                        background: 
                          survey.status === 'active' ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' : 
                          survey.status === 'pending' || survey.status === 'pending_moderation' ? 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' :
                          survey.status === 'draft' ? 'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)' : 
                          survey.status === 'rejected' ? 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)' :
                          survey.status === 'completed' ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' :
                          'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                        color: '#fff',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>{survey.questions?.length || 0}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {survey.responseCount || 0} / {survey.maxResponses || '∞'}
                      </Typography>
                      {survey.maxResponses && (
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(((survey.responseCount || 0) / survey.maxResponses) * 100)}%
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {survey.reward?.moneyAmount > 0 || survey.reward?.pointsAmount > 0 ? (
                      <Box>
                        {survey.reward.moneyAmount > 0 && (
                          <Typography variant="body2">
                            {Number(survey.reward.moneyAmount).toFixed(1)} BYN
                          </Typography>
                        )}
                        {survey.reward.pointsAmount > 0 && (
                          <Typography variant="body2">
                            {Number(survey.reward.pointsAmount).toFixed(1)} баллов
                          </Typography>
                        )}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell>{new Date(survey.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>
                    {survey.publishedAt ? (
                      <Box>
                        <Typography variant="body2">
                          {new Date(survey.publishedAt).toLocaleDateString('ru-RU')}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(survey.publishedAt).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                      {!survey.isProfileSurvey && (
                        <IconButton
                          size="small"
                          title="Интервьюеры"
                          onClick={() => setInterviewersSurvey(survey)}
                          sx={{ color: '#74b9ff' }}
                        >
                          <GroupIcon />
                        </IconButton>
                      )}
                      {survey.isPublicLink && (
                        <IconButton
                          size="small"
                          onClick={() => {
                            const publicUrl = `${window.location.origin}/public/survey/${survey.customSlug || survey.id}`;
                            navigator.clipboard.writeText(publicUrl);
                            setLinkCopied(true);
                          }}
                          title="Скопировать публичную ссылку"
                          sx={{
                            color: '#74b9ff',
                            '&:hover': {
                              background: 'rgba(116, 185, 255, 0.1)'
                            }
                          }}
                        >
                          <LinkIcon />
                        </IconButton>
                      )}
                      <IconButton 
                        size="small" 
                        title="Просмотр ответов" 
                        onClick={() => openResponses(survey, {})}
                        sx={{
                          color: '#74b9ff',
                          '&:hover': {
                            background: 'rgba(116, 185, 255, 0.1)'
                          }
                        }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <Button 
                        size="small" 
                        startIcon={<BarChartIcon />}
                        sx={{ 
                          ml: 1,
                          background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                          color: '#2d3436',
                          fontWeight: 600,
                          fontSize: '0.7rem',
                          boxShadow: '0 2px 8px rgba(253, 203, 110, 0.3)',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(253, 203, 110, 0.4)',
                            transform: 'translateY(-1px)'
                          }
                        }} 
                        onClick={() => openStats(survey)}
                      >Статистика</Button>
                    <IconButton 
                      size="small" 
                      onClick={() => handleDeleteSurvey(survey.id)}
                      sx={{
                        color: '#ff7675',
                        '&:hover': {
                          background: 'rgba(255, 118, 117, 0.1)'
                        }
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {surveyPagination.totalPages > 1 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'center' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} display="flex" justifyContent="center">
                  <Button
                    disabled={surveyPage === 1}
                    onClick={() => setSurveyPage(surveyPage - 1)}
                    sx={{ mr: 1 }}
                  >
                    Предыдущая
                  </Button>
                  <Typography sx={{ mx: 2, alignSelf: 'center' }}>
                    Страница {surveyPage} из {surveyPagination.totalPages}
                  </Typography>
                  <Button
                    disabled={surveyPage === surveyPagination.totalPages}
                    onClick={() => setSurveyPage(surveyPage + 1)}
                    sx={{ ml: 1 }}
                  >
                    Следующая
                  </Button>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="body2" align="center" color="text.secondary">
                    Показано {surveys.length} из {surveyPagination.total} опросов
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </TableContainer>
      )}

      {/* Profile Surveys Tab */}
      {currentTab === 2 && (
        <TableContainer 
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  '& th': {
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: 'none',
                    py: 2
                  }
                }}
              >
                <TableCell>Название</TableCell>
                <TableCell>Категория</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Вопросов</TableCell>
                <TableCell>Ответов</TableCell>
                <TableCell>Создан</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {profileSurveys.map((survey) => (
                <TableRow key={survey.id} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(116, 185, 255, 0.08)',
                    transition: 'background-color 0.3s ease'
                  }
                }}>
                  <TableCell>
                    {survey.title}
                    <Chip 
                      label="Профильный" 
                      size="small" 
                      sx={{ 
                        ml: 1,
                        background: 'linear-gradient(135deg, #a29bfe 0%, #6c5ce7 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        border: 'none'
                      }}
                    />
                  </TableCell>
                  <TableCell>{translateCategory(survey.category)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={
                        survey.status === 'draft' ? 'Черновик' :
                        survey.status === 'active' ? 'Активен' :
                        survey.status === 'pending_moderation' ? 'На модерации' :
                        survey.status === 'completed' ? 'Завершен' :
                        survey.status === 'rejected' ? 'Отклонен' :
                        survey.status
                      }
                      size="small"
                      sx={{
                        background: 
                          survey.status === 'active' ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' :
                          survey.status === 'draft' ? 'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)' :
                          survey.status === 'completed' ? 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' :
                          survey.status === 'pending_moderation' ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' :
                          survey.status === 'rejected' ? 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)' :
                          'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                        color: '#fff',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>{survey.questions?.length || 0}</TableCell>
                  <TableCell>{survey.responseCount || 0}</TableCell>
                  <TableCell>{new Date(survey.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      onClick={() => openResponses(survey, {})}
                      sx={{ 
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        boxShadow: '0 2px 8px rgba(116, 185, 255, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(116, 185, 255, 0.4)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Просмотр
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Completed Surveys Tab */}
      {currentTab === 3 && (
        <TableContainer 
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  '& th': {
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: 'none',
                    py: 2
                  }
                }}
              >
                <TableCell>Название</TableCell>
                <TableCell>Категория</TableCell>
                <TableCell>Заказчик</TableCell>
                <TableCell>Дата публикации</TableCell>
                <TableCell>Дата завершения</TableCell>
                <TableCell>Ответов</TableCell>
                <TableCell>Квота</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {completedSurveys.map((survey) => (
                <TableRow key={survey.id} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(116, 185, 255, 0.08)',
                    transition: 'background-color 0.3s ease'
                  }
                }}>
                  <TableCell>
                    {survey.title}
                    {survey.isPublicLink && (
                      <Chip 
                        label="Публичная ссылка" 
                        size="small" 
                        sx={{ 
                          ml: 1,
                          background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    )}
                    {survey.isExternalSurvey && (
                      <Chip 
                        label="Внешний опрос" 
                        size="small" 
                        sx={{ 
                          ml: 1,
                          background: 'linear-gradient(135deg, #fdcb6e 0%, #e17055 100%)',
                          color: '#fff',
                          fontWeight: 600,
                          border: 'none'
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell>{translateCategory(survey.category)}</TableCell>
                  <TableCell>
                    {usersCache[survey.clientId]?.fullName || survey.clientId || 'Неизвестно'}
                  </TableCell>
                  <TableCell>
                    {survey.publishedAt ? new Date(survey.publishedAt).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>
                    {survey.completedAt ? new Date(survey.completedAt).toLocaleDateString() : '-'}
                  </TableCell>
                  <TableCell>{survey.responseCount || 0}</TableCell>
                  <TableCell>
                    {survey.responseCount || 0} / {survey.maxResponses || '∞'}
                  </TableCell>
                  <TableCell align="right">
                    {!survey.isProfileSurvey && (
                        <IconButton
                          size="small"
                          title="Интервьюеры"
                          onClick={() => setInterviewersSurvey(survey)}
                          sx={{ color: '#74b9ff' }}
                        >
                          <GroupIcon />
                        </IconButton>
                      )}
                      {survey.isPublicLink && (
                      <IconButton
                        size="small"
                        onClick={() => {
                          const publicUrl = `${window.location.origin}/public/survey/${survey.customSlug || survey.id}`;
                          navigator.clipboard.writeText(publicUrl);
                          setLinkCopied(true);
                        }}
                        title="Скопировать публичную ссылку"
                        sx={{ 
                          mr: 1,
                          color: '#74b9ff',
                          '&:hover': {
                            background: 'rgba(116, 185, 255, 0.1)'
                          }
                        }}
                      >
                        <LinkIcon />
                      </IconButton>
                    )}
                    <Button
                      size="small"
                      onClick={() => openResponses(survey, {})}
                      sx={{
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: '#fff',
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        boxShadow: '0 2px 8px rgba(116, 185, 255, 0.3)',
                        '&:hover': {
                          boxShadow: '0 4px 12px rgba(116, 185, 255, 0.4)',
                          transform: 'translateY(-1px)'
                        }
                      }}
                    >
                      Просмотр
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Withdrawals Tab */}
      {currentTab === 4 && (
        <TableContainer 
          component={Paper}
          sx={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: { xs: '16px', md: '24px' },
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden'
          }}
        >
          <Table>
            <TableHead>
              <TableRow
                sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                  '& th': {
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: '0.75rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    borderBottom: 'none',
                    py: 2
                  }
                }}
              >
                <TableCell>Пользователь</TableCell>
                <TableCell>Сумма (BYN)</TableCell>
                <TableCell>Метод</TableCell>
                <TableCell>Детали</TableCell>
                <TableCell>Чек</TableCell>
                <TableCell>Статус</TableCell>
                <TableCell>Запрошено</TableCell>
                <TableCell align="right">Действия</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((wd) => (
                <TableRow key={wd.id} sx={{
                  '&:hover': {
                    backgroundColor: 'rgba(116, 185, 255, 0.08)',
                    transition: 'background-color 0.3s ease'
                  }
                }}>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {wd.userName || 'Unknown'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {wd.userEmail}
                      </Typography>
                      {wd.userDeleted && wd.userPhone && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {wd.userPhone}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {wd.amount}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {wd.method === 'bank' ? 'Банковская карта' : 'Мобильный'}
                  </TableCell>
                  <TableCell>
                    {wd.details && (
                      <Box>
                        {wd.method === 'bank' && (
                          <>
                            <Typography variant="caption" display="block">
                              Карта: {wd.details.cardNumber}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Имя: {wd.details.cardHolder}
                            </Typography>
                          </>
                        )}
                        {wd.method === 'mobile' && (
                          <Typography variant="caption">
                            Тел: {wd.details.mobileNumber}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell>
                    {wd.contractFile && wd.contractFile.fileUrl && (
                      <Button
                        size="small"
                        variant="outlined"
                        href={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}${wd.contractFile.fileUrl}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{
                          fontSize: '0.7rem',
                          textTransform: 'none',
                          borderColor: '#0984e3',
                          color: '#0984e3',
                          '&:hover': {
                            borderColor: '#0652DD',
                            background: 'rgba(9, 132, 227, 0.05)'
                          }
                        }}
                      >
                        Скачать
                      </Button>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={wd.status === 'pending' ? 'В ожидании' : wd.status === 'completed' ? 'Одобрено' : 'Отклонено'} 
                      size="small" 
                      sx={{
                        background: 
                          wd.status === 'completed' ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' :
                          wd.status === 'pending' ? '#fdcb6e' :
                          'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                        color: wd.status === 'pending' ? '#744210' : '#fff',
                        fontWeight: 600
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(wd.createdAt).toLocaleString('ru-RU')}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {wd.status === 'pending' && (
                      <>
                        <Button 
                          size="small" 
                          sx={{ 
                            mr: 1,
                            background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            boxShadow: '0 2px 8px rgba(86, 171, 47, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(86, 171, 47, 0.4)',
                              transform: 'translateY(-1px)'
                            }
                          }} 
                          onClick={() => handleApproveWithdrawal(wd.id)}
                        >Одобрить</Button>
                        <Button 
                          size="small" 
                          sx={{
                            background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            boxShadow: '0 2px 8px rgba(255, 118, 117, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(255, 118, 117, 0.4)',
                              transform: 'translateY(-1px)'
                            }
                          }}
                          onClick={() => handleRejectWithdrawal(wd.id)}
                        >Отклонить</Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Rewards Tab */}
      {currentTab === 5 && (
        <Paper sx={{ 
          p: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '24px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2d3748' }}>Подарки за баллы</Typography>
            <Button 
              onClick={() => handleOpenRewardDialog()}
              sx={{
                background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                color: '#fff',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(86, 171, 47, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(86, 171, 47, 0.4)',
                  transform: 'translateY(-2px)'
                }
              }}
            >Добавить подарок</Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Название</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Баллы</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Остаток</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Активна</TableCell>
                  <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rewards.map((rw) => (
                  <TableRow key={rw.id} sx={{
                    '&:hover': {
                      backgroundColor: 'rgba(116, 185, 255, 0.08)',
                      transition: 'background-color 0.3s ease'
                    }
                  }}>
                    <TableCell>{rw.title}</TableCell>
                    <TableCell>{rw.points}</TableCell>
                    <TableCell>{rw.stock}</TableCell>
                    <TableCell>
                      <Chip 
                        size="small" 
                        label={rw.active ? 'Да' : 'Нет'} 
                        sx={{
                          background: rw.active ? 
                            'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' : 
                            'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                          color: '#fff',
                          fontWeight: 600
                        }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton 
                        size="small" 
                        onClick={() => handleOpenRewardDialog(rw)} 
                        sx={{ 
                          mr: 1,
                          color: '#74b9ff',
                          '&:hover': {
                            background: 'rgba(116, 185, 255, 0.1)'
                          }
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleDeleteReward(rw.id)}
                        sx={{
                          color: '#ff7675',
                          '&:hover': {
                            background: 'rgba(255, 118, 117, 0.1)'
                          }
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Reward Claims Tab */}
      {currentTab === 6 && (
        <Paper sx={{ 
          p: 2,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '16px', md: '24px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, color: '#2d3748' }}>Заявки на отправку подарков</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Пользователь</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Подарок</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Баллы</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Дата заявки</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Адрес доставки</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Статус</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rewardClaims.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">Нет заявок на подарки</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  rewardClaims.map((claim) => {
                    const reward = rewards.find(r => r.id === claim.rewardId);
                    const userName = claim.user ? claim.user.fullName || claim.user.email || claim.userId : claim.userId;
                    return (
                      <TableRow key={claim.id} sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(116, 185, 255, 0.08)',
                          transition: 'background-color 0.3s ease'
                        }
                      }}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2">{userName}</Typography>
                            <Tooltip title="Открыть информацию о пользователе">
                              <IconButton 
                                size="small" 
                                onClick={async () => {
                                  try {
                                    const token = localStorage.getItem('token');
                                    const userRes = await axios.get(`${API_BASE}/users/${claim.userId}`, {
                                      headers: { Authorization: `Bearer ${token}` }
                                    });
                                    setSelectedUser(userRes.data);
                                    setUserDialogOpen(true);
                                    loadUserProfileSurveyData(userRes.data);
                                  } catch (error) {
                                    console.error('Error fetching user:', error);
                                    alert('Не удалось загрузить информацию о пользователе');
                                  }
                                }}
                              >
                                <InfoIcon fontSize="small" color="primary" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                        <TableCell>{reward?.title || 'Неизвестно'}</TableCell>
                        <TableCell>{reward?.points || 0}</TableCell>
                        <TableCell>
                          {claim.createdAt ? new Date(claim.createdAt).toLocaleString() : '-'}
                        </TableCell>
                        <TableCell>
                          {claim.deliveryDetails ? (
                            <Box sx={{ fontSize: '0.875rem' }}>
                              <div><strong>{claim.deliveryDetails.fullName}</strong></div>
                              <div>{claim.deliveryDetails.phone}</div>
                              <div>
                                {claim.deliveryDetails.street}, {claim.deliveryDetails.city}
                              </div>
                              <div>
                                {claim.deliveryDetails.country}, {claim.deliveryDetails.postalCode}
                              </div>
                            </Box>
                          ) : 'Нет данных'}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip 
                              size="small" 
                              label={
                                claim.status === 'pending' ? 'В ожидании' :
                                claim.status === 'shipped' ? 'Отправлено' :
                                claim.status === 'delivered' ? 'Доставлено' :
                                claim.status === 'cancelled' ? 'Отменено' :
                                claim.status
                              }
                              sx={{
                                background: 
                                  claim.status === 'pending' ? 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)' :
                                  claim.status === 'shipped' ? 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)' :
                                  claim.status === 'delivered' ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)' :
                                  'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                                color: '#fff',
                                fontWeight: 600
                              }}
                            />
                            {(claim.status === 'shipped' || claim.status === 'delivered') && claim.trackingNumber && (
                              <Tooltip 
                                title={
                                  <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Трек-номер:</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>{claim.trackingNumber}</Typography>
                                    {claim.shippingComment && (
                                      <>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Комментарий:</Typography>
                                        <Typography variant="body2">{claim.shippingComment}</Typography>
                                      </>
                                    )}
                                  </Box>
                                }
                              >
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {claim.status === 'cancelled' && claim.rejectionComment && (
                              <Tooltip title={`Причина отмены: ${claim.rejectionComment}`}>
                                <IconButton size="small">
                                  <InfoIcon fontSize="small" color="error" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {claim.status === 'pending' && (
                            <>
                              <Button
                                size="small"
                                onClick={() => openClaimStatusDialog(claim, 'shipped')}
                                sx={{ 
                                  mr: 1, 
                                  mb: 1,
                                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  boxShadow: '0 2px 8px rgba(116, 185, 255, 0.3)',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(116, 185, 255, 0.4)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                Отправлено
                              </Button>
                              <Button
                                size="small"
                                onClick={() => openClaimStatusDialog(claim, 'cancelled')}
                                sx={{
                                  background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                                  color: '#fff',
                                  fontWeight: 600,
                                  fontSize: '0.7rem',
                                  boxShadow: '0 2px 8px rgba(255, 118, 117, 0.3)',
                                  '&:hover': {
                                    boxShadow: '0 4px 12px rgba(255, 118, 117, 0.4)',
                                    transform: 'translateY(-1px)'
                                  }
                                }}
                              >
                                Отменить
                              </Button>
                            </>
                          )}
                          {claim.status === 'shipped' && (
                            <Button
                              size="small"
                              onClick={() => openClaimStatusDialog(claim, 'delivered')}
                              sx={{
                                background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                                color: '#fff',
                                fontWeight: 600,
                                fontSize: '0.7rem',
                                boxShadow: '0 2px 8px rgba(86, 171, 47, 0.3)',
                                '&:hover': {
                                  boxShadow: '0 4px 12px rgba(86, 171, 47, 0.4)',
                                  transform: 'translateY(-1px)'
                                }
                              }}
                            >
                              Доставлено
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}


      <Dialog 
        open={userDialogOpen} 
        onClose={() => setUserDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>Информация о пользователе</DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box>
              <Typography><strong>Имя:</strong> {selectedUser.fullName}</Typography>
              <Typography><strong>Email:</strong> {selectedUser.email}</Typography>
              <Typography><strong>Телефон:</strong> {selectedUser.phone || 'Н/Д'}</Typography>
              <Typography><strong>Роль:</strong> {selectedUser.role === 'admin' ? 'Администратор' : selectedUser.role === 'client' ? 'Заказчик' : 'Респондент'}</Typography>
              <Typography><strong>Баланс:</strong> {selectedUser.balance || 0} BYN</Typography>
              <Typography><strong>Регистрация:</strong> {new Date(selectedUser.createdAt).toLocaleString()}</Typography>

              {/* Profile survey completion */}
              {userProfileCompletion && (
                <Paper sx={{ p:2, mt:2 }} elevation={0}>
                  <Typography variant="subtitle1" gutterBottom><strong>Профильные опросы</strong></Typography>
                  <Typography variant="body2" sx={{ mb:1 }}>
                    Завершено: {userProfileCompletion.completedProfileSurveys}/{userProfileCompletion.totalProfileSurveys} ({userProfileCompletion.completionPercentage}%)
                  </Typography>
                  <Box sx={{ height:6, bgcolor:'grey.300', borderRadius:3, overflow:'hidden', mb:2 }}>
                    <Box sx={{ width:`${userProfileCompletion.completionPercentage}%`, bgcolor:'success.main', height:'100%' }} />
                  </Box>
                  {userProfileSurveys.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Нет активных профильных опросов</Typography>
                  ) : (
                    <Box>
                      {userProfileSurveys.map(sv => {
                        const answered = userProfileAnswers.find(a => a.surveyId === sv.id);
                        return (
                          <Paper key={sv.id} sx={{ p:1, mb:1 }} variant="outlined">
                            <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                              <Box sx={{ flex: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight:'bold' }}>{sv.title}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {sv.profileCategory ? `Категория: ${sv.profileCategory}` : ''}
                                </Typography>
                              </Box>
                              <Chip size="small" color={answered ? 'success' : 'warning'} label={answered ? 'Заполнен' : 'Ожидает'} />
                            </Box>
                            {answered && sv.questions && (
                              <Box sx={{ mt:1, pl:1, borderLeft:'2px solid', borderColor:'success.light' }}>
                                {Object.entries(answered.answers || {}).map(([qId, val]) => {
                                  const question = sv.questions.find(q => q.id === qId);
                                  const questionText = question?.text || qId;
                                  const answerText = Array.isArray(val) ? val.join(', ') : String(val);
                                  return (
                                    <Box key={qId} sx={{ mb:0.5 }}>
                                      <Typography variant="caption" display="block" sx={{ fontWeight:500, color:'text.primary' }}>
                                        {questionText}
                                      </Typography>
                                      <Typography variant="caption" display="block" color="text.secondary" sx={{ pl:1 }}>
                                        → {answerText}
                                      </Typography>
                                    </Box>
                                  );
                                })}
                              </Box>
                            )}
                          </Paper>
                        );
                      })}
                    </Box>
                  )}
                </Paper>
              )}
              {loadingProfileData && (
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mt:2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="caption">Загрузка профильных данных...</Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* User Edit Dialog */}
      <Dialog 
        open={userEditDialogOpen} 
        onClose={handleCloseUserEdit} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>
          Редактировать пользователя
        </DialogTitle>
        <DialogContent>
          {userEditError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{userEditError}</Alert>
          )}
          {userEditSuccess && (
            <Alert severity="success" sx={{ mb: 2, mt: 1 }}>{userEditSuccess}</Alert>
          )}
          <TextField 
            fullWidth 
            label="Email" 
            type="email"
            sx={{ mt: 2 }} 
            value={editUserData.email} 
            onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })} 
          />
          <TextField 
            fullWidth 
            label="Имя" 
            sx={{ mt: 2 }} 
            value={editUserData.fullName} 
            onChange={(e) => setEditUserData({ ...editUserData, fullName: e.target.value })} 
          />
          <TextField 
            fullWidth 
            label="Телефон" 
            sx={{ mt: 2 }} 
            value={editUserData.phone} 
            onChange={(e) => setEditUserData({ ...editUserData, phone: e.target.value })} 
          />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                label="Баланс (BYN)" 
                type="number"
                inputProps={{ step: '0.01' }}
                value={editUserData.balance} 
                onChange={(e) => setEditUserData({ ...editUserData, balance: e.target.value })} 
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                label="Баллы" 
                type="number"
                inputProps={{ step: '0.1' }}
                value={editUserData.points} 
                onChange={(e) => setEditUserData({ ...editUserData, points: e.target.value })} 
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCloseUserEdit} sx={{ color: '#666' }}>
            Отмена
          </Button>
          <Button 
            variant="contained" 
            onClick={handleSaveUserEdit}
            disabled={savingUserEdit}
            sx={{
              background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
              color: '#fff',
              fontWeight: 600,
              boxShadow: '0 4px 12px rgba(116, 185, 255, 0.3)',
              '&:hover': {
                boxShadow: '0 6px 20px rgba(116, 185, 255, 0.4)'
              }
            }}
          >
            {savingUserEdit ? <CircularProgress size={20} color="inherit" /> : 'Сохранить'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog 
        open={rewardDialogOpen} 
        onClose={handleCloseRewardDialog} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>{editingReward ? 'Редактировать подарок' : 'Новый подарок'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Название" sx={{ mt: 1 }} value={newReward.title} onChange={(e) => setNewReward({ ...newReward, title: e.target.value })} />
          <TextField fullWidth label="Описание" sx={{ mt: 2 }} multiline rows={3} value={newReward.description} onChange={(e) => setNewReward({ ...newReward, description: e.target.value })} />
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                label="Баллы" 
                type="number" 
                value={newReward.points} 
                onChange={(e) => setNewReward({ ...newReward, points: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField 
                fullWidth 
                label="Остаток" 
                type="number" 
                value={newReward.stock} 
                onChange={(e) => setNewReward({ ...newReward, stock: parseInt(e.target.value) || 0 })}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
          </Grid>
          
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Изображение подарка</Typography>
            <Button
              variant="outlined"
              component="label"
              fullWidth
              sx={{ mb: 1 }}
            >
              Выбрать файл
              <input
                type="file"
                hidden
                accept="image/*"
                onChange={handleRewardImageChange}
              />
            </Button>
            {rewardImagePreview && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <img 
                  src={rewardImagePreview} 
                  alt="Preview" 
                  style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'contain' }}
                />
              </Box>
            )}
            {rewardImageFile && (
              <Typography variant="caption" color="text.secondary">
                Файл: {rewardImageFile.name}
              </Typography>
            )}
          </Box>
          
          <FormControlLabel
            control={
              <Switch
                checked={newReward.active}
                onChange={(e) => setNewReward({ ...newReward, active: e.target.checked })}
              />
            }
            label="Активен (доступен для заказа)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseRewardDialog} disabled={creatingReward}>Отмена</Button>
          <Button 
            variant="contained" 
            onClick={handleSaveReward}
            disabled={creatingReward}
            startIcon={creatingReward ? <CircularProgress size={20} /> : null}
          >
            {creatingReward ? 'Загрузка...' : (editingReward ? 'Сохранить' : 'Создать')}
          </Button>
        </DialogActions>
      </Dialog>

          {/* Claim Status Dialog */}
          <Dialog 
            open={claimStatusDialogOpen} 
            onClose={() => setClaimStatusDialogOpen(false)} 
            maxWidth="sm" 
            fullWidth
            PaperProps={{
              sx: {
                background: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                borderRadius: '24px',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
              }
            }}
          >
            <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>Обновление статуса заявки</DialogTitle>
            <DialogContent>
              {claimStatusTarget && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  <strong>Пользователь:</strong> {claimStatusTarget.user ? (claimStatusTarget.user.fullName || claimStatusTarget.user.email) : claimStatusTarget.userId}
                  <br />
                  <strong>Дата заявки:</strong> {claimStatusTarget.createdAt ? new Date(claimStatusTarget.createdAt).toLocaleString() : '-'}
                  <br />
                  <strong>Новый статус:</strong> {(
                    claimStatusNewStatus === 'shipped' ? 'Отправлено' :
                    claimStatusNewStatus === 'delivered' ? 'Доставлено' :
                    claimStatusNewStatus === 'cancelled' ? 'Отменено' : claimStatusNewStatus
                  )}
                </Typography>
              )}
              {claimStatusNewStatus === 'shipped' && (
                <>
                  <TextField
                    fullWidth
                    label="Трек-номер"
                    sx={{ mb: 2 }}
                    value={claimTrackingNumber}
                    onChange={(e) => setClaimTrackingNumber(e.target.value)}
                  />
                  <TextField
                    fullWidth
                    label="Комментарий к отправке"
                    multiline
                    minRows={2}
                    value={claimShippingComment}
                    onChange={(e) => setClaimShippingComment(e.target.value)}
                  />
                </>
              )}
              {claimStatusNewStatus === 'cancelled' && (
                <TextField
                  fullWidth
                  label="Причина отмены"
                  multiline
                  minRows={2}
                  value={claimRejectionComment}
                  onChange={(e) => setClaimRejectionComment(e.target.value)}
                />
              )}
              {claimStatusError && (
                <Alert severity="error" sx={{ mt: 2 }}>{claimStatusError}</Alert>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setClaimStatusDialogOpen(false)} sx={{
                color: '#666',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)'
                }
              }}>Отмена</Button>
              <Button 
                variant="contained" 
                onClick={submitClaimStatus} 
                disabled={claimStatusSubmitting}
                sx={{
                  background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                  color: '#fff',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(86, 171, 47, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 20px rgba(86, 171, 47, 0.4)',
                    transform: 'translateY(-2px)'
                  },
                  '&:disabled': {
                    background: '#ccc',
                    color: '#666'
                  }
                }}
              >
                {claimStatusSubmitting ? <CircularProgress size={22} /> : 'Сохранить'}
              </Button>
            </DialogActions>
          </Dialog>

      <Dialog open={responsesOpen} onClose={() => setResponsesOpen(false)} maxWidth="lg" fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{selectedSurvey ? `Ответы для: ${selectedSurvey.title}` : 'Ответы'}</span>
            {!responsesLoading && !responsesError && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileIcon />}
                size="small"
                sx={{ borderColor: '#0984e3', color: '#0984e3', fontWeight: 600 }}
              >
                Импорт Excel
                <input
                  hidden
                  type="file"
                  accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={previewResponseImport}
                />
              </Button>
              {(respFilterInterviewer || respFilterStatus) && (
                <Button variant="text" size="small" startIcon={<ExcelIcon />} onClick={exportAllResponsesToExcel} sx={{ color: '#56ab2f' }}>
                  Экспорт всех
                </Button>
              )}
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={responses.length === 0}
                onClick={() => handleExportResponsesToSav(responses)}
                size="small"
                sx={{
                  borderColor: '#0984e3',
                  color: '#0984e3',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#065a9e',
                    backgroundColor: 'rgba(9, 132, 227, 0.08)'
                  }
                }}
              >
                Экспорт в SPSS
              </Button>
              <Button
                variant="outlined"
                startIcon={<ExcelIcon />}
                disabled={responses.length === 0}
                onClick={() => handleExportResponsesToExcel(responses, (respFilterInterviewer || respFilterStatus) ? 'filtered' : 'all')}
                size="small"
                sx={{
                  borderColor: '#56ab2f',
                  color: '#56ab2f',
                  fontWeight: 600,
                  '&:hover': {
                    borderColor: '#2d7a1f',
                    backgroundColor: 'rgba(86, 171, 47, 0.08)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                {(respFilterInterviewer || respFilterStatus) ? `Экспорт (отфильтровано: ${responses.length})` : 'Экспорт в Excel'}
              </Button>
              </Box>
            )}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {responsesLoading && (
            <Typography variant="body2">Загрузка ответов…</Typography>
          )}
          {responsesError && (
            <Typography color="error">{responsesError}</Typography>
          )}
          {selectedSurvey && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 2 }}>
              <TextField select size="small" label="Интервьюер" value={respFilterInterviewer} sx={{ minWidth: 220 }}
                onChange={(e) => openResponses(selectedSurvey, { interviewerId: e.target.value, status: respFilterStatus })}>
                <MenuItem value="">Все</MenuItem>
                {respInterviewerOptions.map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
              </TextField>
              <TextField select size="small" label="Статус" value={respFilterStatus} sx={{ minWidth: 200 }}
                onChange={(e) => openResponses(selectedSurvey, { interviewerId: respFilterInterviewer, status: e.target.value })}>
                <MenuItem value="">Все</MenuItem>
                {['completed', 'under_review', 'approved', 'rejected', 'disqualified', 'pending'].map((st) => (
                  <MenuItem key={st} value={st}>{getResponseStatusLabel(st)}</MenuItem>
                ))}
              </TextField>
              <Typography variant="body2" sx={{ color: '#4a5568' }}>Найдено: {responses.length}</Typography>
              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" variant="outlined" disabled={respSelectableIds.length === 0 || responsesLoading}
                onClick={() => setRespSelected(respSelectableIds)}>
                Выбрать все по фильтру ({respSelectableIds.length})
              </Button>
              {respSelected.length > 0 && (
                <Button size="small" onClick={() => setRespSelected([])}>Снять выбор</Button>
              )}
              <Button size="small" variant="contained" color="error" disabled={respSelected.length === 0 || responsesLoading}
                onClick={openBulkReject}>
                Отклонить выбранные ({respSelected.length})
              </Button>
            </Box>
          )}
          {!responsesLoading && !responsesError && (
            <>
            <Table size="small">
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)'
                }}>
                  <TableCell padding="checkbox">
                    <Checkbox size="small" sx={{ color: '#fff', '&.Mui-checked': { color: '#fff' } }}
                      checked={respPageAllSelected} disabled={respPageSelectable.length === 0}
                      onChange={toggleRespPageSelected} inputProps={{ 'aria-label': 'Выбрать на странице' }} />
                  </TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Пользователь</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Завершено</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Время (сек)</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Интервьюер</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Флаги</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Статус</TableCell>
                  <TableCell align="right" sx={{ color: '#fff', fontWeight: 700, fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {respPageRows.map((r) => {
                  const flags = r.flags || {};
                  const isAnonymous = !r.userId;
                  const user = r.userId ? usersCache[r.userId] : null;
                  const userLabel = isAnonymous ? 'Анонимно' : (user?.fullName || user?.email || r.userId);
                  return (
                    <TableRow key={r.id} sx={{
                      '&:hover': {
                        backgroundColor: 'rgba(116, 185, 255, 0.08)',
                        transition: 'background-color 0.3s ease'
                      }
                    }}>
                      <TableCell padding="checkbox">
                        <Checkbox size="small" disabled={r.status === 'rejected'}
                          checked={respSelected.includes(r.id)} onChange={() => toggleRespSelected(r.id)} />
                      </TableCell>
                      <TableCell>{userLabel}</TableCell>
                      <TableCell>{r.completedAt ? new Date(r.completedAt).toLocaleString() : '-'}</TableCell>
                      <TableCell>{r.timeSpent ?? 'Н/Д'}</TableCell>
                      <TableCell>{r.interviewerName || '-'}</TableCell>
                      <TableCell>
                        {flags.fastCompletion && (
                          <Chip 
                            size="small" 
                            label="Быстро" 
                            sx={{ 
                              mr: 0.5,
                              background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)',
                              color: '#fff',
                              fontWeight: 600
                            }} 
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip 
                          size="small" 
                          label={getResponseStatusLabel(r.status || 'pending')} 
                          sx={{
                            background: r.status === 'approved' 
                              ? 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)'
                              : r.status === 'rejected'
                              ? 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)'
                              : r.status === 'under_review'
                              ? 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 100%)'
                              : 'linear-gradient(135deg, #dfe6e9 0%, #b2bec3 100%)',
                            color: '#fff',
                            fontWeight: 600
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button 
                          size="small" 
                          sx={{ 
                            mr: 1,
                            background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            boxShadow: '0 2px 8px rgba(116, 185, 255, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(116, 185, 255, 0.4)',
                              transform: 'translateY(-1px)'
                            }
                          }} 
                          onClick={() => { setSelectedResponse(r); setResponseDetailOpen(true); }}
                        >Просмотр</Button>
                        <Button 
                          size="small" 
                          disabled={r.status === 'rejected'} 
                          onClick={() => rejectResponse(r.id)}
                          sx={{
                            mr: 1,
                            background: 'linear-gradient(135deg, #ff7675 0%, #d63031 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            boxShadow: '0 2px 8px rgba(255, 118, 117, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(255, 118, 117, 0.4)',
                              transform: 'translateY(-1px)'
                            },
                            '&:disabled': {
                              background: '#ccc',
                              color: '#666'
                            }
                          }}
                        >Отклонить</Button>
                        <Button 
                          size="small" 
                          disabled={r.status !== 'under_review'} 
                          onClick={() => approveResponse(r.id)}
                          sx={{
                            background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                            color: '#fff',
                            fontWeight: 600,
                            fontSize: '0.7rem',
                            boxShadow: '0 2px 8px rgba(86, 171, 47, 0.3)',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(86, 171, 47, 0.4)',
                              transform: 'translateY(-1px)'
                            },
                            '&:disabled': {
                              background: '#ccc',
                              color: '#666'
                            }
                          }}
                        >Одобрить</Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <TablePagination component="div" count={responses.length} page={respPage}
              onPageChange={(e, p) => setRespPage(p)} rowsPerPage={respRowsPerPage}
              onRowsPerPageChange={(e) => { setRespRowsPerPage(parseInt(e.target.value, 10)); setRespPage(0); }}
              rowsPerPageOptions={[25, 50, 100, 200]} labelRowsPerPage="Строк на странице" />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponsesOpen(false)} sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={responseImportOpen} onClose={closeResponseImport} maxWidth="sm" fullWidth>
        <DialogTitle>Импорт ответов из Excel</DialogTitle>
        <DialogContent dividers>
          {responseImportFile && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              Файл: <b>{responseImportFile.name}</b>
            </Typography>
          )}
          {responseImportBusy && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={22} />
              <Typography variant="body2">
                {responseImportPreview ? 'Импортирую ответы…' : 'Проверяю файл…'}
              </Typography>
            </Box>
          )}
          {responseImportError && (
            <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }}>
              {responseImportError}
            </Alert>
          )}
          {responseImportResult && (
            <Alert severity="success">
              Импортировано ответов: <b>{responseImportResult.imported}</b>.
              {responseImportResult.recovered > 0
                ? <> Восстановлен незавершённый пакет: <b>{responseImportResult.recovered}</b>.</>
                : null}
              {' '}Ответов в опросе: <b>{responseImportResult.responseCount}</b>.
            </Alert>
          )}
          {responseImportPreview && !responseImportResult && (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Ответы будут добавлены как обычные анонимные завершённые ответы. Аккаунты,
                вознаграждения, уведомления и заявки Bitrix не создаются. Заказчик не увидит
                пометку об импорте.
              </Alert>
              <Typography sx={{ mb: 0.5 }}>
                Строк с ответами: <b>{responseImportPreview.parsedRows}</b>
              </Typography>
              <Typography sx={{ mb: 0.5 }}>
                Новых ответов: <b>{responseImportPreview.willImport}</b>
              </Typography>
              {responseImportPreview.willRecover > 0 && (
                <Typography sx={{ mb: 0.5 }}>
                  Будет восстановлено после прерванного импорта: <b>{responseImportPreview.willRecover}</b>
                </Typography>
              )}
              <Typography sx={{ mb: 0.5 }}>
                Повторов, которые будут пропущены: <b>{responseImportPreview.duplicateRows}</b>
              </Typography>
              <Typography sx={{ mb: 0.5 }}>
                Сопоставлено вопросов: <b>{responseImportPreview.matchedQuestions}</b>
                {' '}из {responseImportPreview.totalQuestions}
              </Typography>
              <Typography sx={{ mb: 1 }}>
                Счётчик ответов: {responseImportPreview.currentResponseCount}
                {' → '}<b>{responseImportPreview.resultingResponseCount}</b>
                {responseImportPreview.maxResponses ? ` (лимит: ${responseImportPreview.maxResponses})` : ''}
              </Typography>
              {responseImportPreview.willComplete && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  После импорта опрос достигнет лимита и будет завершён.
                </Alert>
              )}
              {(responseImportPreview.warnings || []).map((warning, index) => (
                <Alert severity="warning" key={`${warning}-${index}`} sx={{ mb: 1 }}>
                  {warning}
                </Alert>
              ))}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeResponseImport} disabled={responseImportBusy}>
            {responseImportResult ? 'Закрыть' : 'Отмена'}
          </Button>
          {!responseImportResult && responseImportPreview && (
            <Button
              variant="contained"
              onClick={confirmResponseImport}
              disabled={responseImportBusy || responseImportPreview.willApply === 0}
            >
              Применить {responseImportPreview.willApply}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => !bulkBusy && setBulkOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Массовое отклонение ответов</DialogTitle>
        <DialogContent dividers>
          {bulkBusy && !bulkPreview && <CircularProgress size={24} />}
          {bulkError && <Alert severity="error" sx={{ mb: 2 }}>{bulkError}</Alert>}
          {bulkResult ? (
            <Alert severity={bulkResult.errors ? 'warning' : 'success'}>
              Отклонено: {bulkResult.rejected}. Пропущено (уже отклонены): {bulkResult.skipped}. Ошибок: {bulkResult.errors}.
              {' '}Списано: {bulkResult.reversed?.money || 0} BYN, {bulkResult.reversed?.points || 0} баллов.
            </Alert>
          ) : bulkPreview && (
            <>
              <Typography sx={{ mb: 1 }}>Будет отклонено ответов: <b>{bulkPreview.count}</b>
                {bulkPreview.alreadyRejected ? ` (уже отклонены и будут пропущены: ${bulkPreview.alreadyRejected})` : ''}</Typography>
              <Typography sx={{ mb: 1 }}>С начисленной наградой: {bulkPreview.withReward}. Будет списано: <b>{bulkPreview.money} BYN</b> и <b>{bulkPreview.points} баллов</b>.</Typography>
              {bulkPreview.disqualified > 0 && (
                <Typography sx={{ mb: 1 }} variant="body2">Из них дисквалифицированных: {bulkPreview.disqualified} (на счётчик и квоты не влияют).</Typography>
              )}
              <Alert severity="warning" sx={{ mb: 2 }}>Действие необратимо: респонденты получат уведомление, награда будет списана, счётчик ответов и квоты уменьшатся — закрытые квоты могут снова открыться.</Alert>
              <TextField fullWidth multiline minRows={2} label="Причина (необязательно)" value={bulkReason}
                onChange={(e) => setBulkReason(e.target.value)} sx={{ mb: 2 }} inputProps={{ maxLength: 1000 }} />
              <TextField fullWidth label={`Для подтверждения введите число ${bulkPreview.count}`} value={bulkTypedCount}
                onChange={(e) => setBulkTypedCount(e.target.value.trim())} />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)} disabled={bulkBusy}>{bulkResult ? 'Закрыть' : 'Отмена'}</Button>
          {!bulkResult && (
            <Button color="error" variant="contained" onClick={confirmBulkReject}
              disabled={bulkBusy || !bulkPreview || bulkPreview.count === 0 || bulkTypedCount !== String(bulkPreview.count)}>
              {bulkBusy ? 'Отклоняю…' : 'Отклонить'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Response Detail Dialog */}
      <Dialog 
        open={responseDetailOpen} 
        onClose={() => setResponseDetailOpen(false)} 
        maxWidth="md" 
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#2d3748', fontSize: '1.5rem' }}>Детали ответа</DialogTitle>
        <DialogContent dividers>
          {selectedResponse && selectedSurvey ? (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Информация о респонденте
              </Typography>
              {selectedResponse.user ? (
                <Box sx={{ mb: 2 }}>
                  {selectedResponse.user.fullName && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Имя:</strong> {selectedResponse.user.fullName}
                    </Typography>
                  )}
                  {selectedResponse.user.email && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Email:</strong> {selectedResponse.user.email}
                    </Typography>
                  )}
                  {selectedResponse.user.age && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Возраст:</strong> {selectedResponse.user.age} лет
                    </Typography>
                  )}
                  {selectedResponse.user.gender && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Пол:</strong> {selectedResponse.user.gender}
                    </Typography>
                  )}
                  {(selectedResponse.user.city || selectedResponse.user.region) && (
                    <Typography variant="body2" gutterBottom>
                      <strong>Местоположение:</strong> {selectedResponse.user.city}{selectedResponse.user.region ? `, ${selectedResponse.user.region}` : ''}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                  ID: {selectedResponse.userId || 'Анонимно'}
                </Typography>
              )}
              <Typography variant="body2" gutterBottom>
                <strong>Завершено:</strong> {selectedResponse.completedAt ? new Date(selectedResponse.completedAt).toLocaleString() : '-'}
              </Typography>
              <Typography variant="body2" gutterBottom sx={{ mb: 2 }}>
                <strong>Время:</strong> {selectedResponse.timeSpent != null ? `${selectedResponse.timeSpent} сек` : 'Н/Д'}
              </Typography>
              {(selectedSurvey.questions || []).map((q) => {
                const a = selectedResponse.answers ? selectedResponse.answers[q.id] : undefined;
                const renderAnswer = () => {
                  if (a === undefined || a === null) return <em>Нет ответа</em>;
                  if (q.type === 'file' && a) {
                    const fileUrl = typeof a === 'object' ? a.fileUrl : a;
                    const fileName = typeof a === 'object' && a.originalName ? a.originalName : (typeof a === 'string' ? a.split('/').pop() : 'Файл');
                    return (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<FileIcon />}
                          label={fileName}
                          onClick={() => handleDownloadFile(fileUrl)}
                          clickable
                          color="primary"
                          size="small"
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleDownloadFile(fileUrl)}
                          color="primary"
                          title="Скачать файл"
                        >
                          <DownloadIcon />
                        </IconButton>
                      </Box>
                    );
                  }
                  if (q.type === 'ranking' && Array.isArray(a)) {
                    // Ранжирование: показываем порядок со стрелками
                    return a.join(' → ');
                  }
                  if (q.type === 'matrix' && a && typeof a === 'object' && !Array.isArray(a)) {
                    // Матрица: показываем каждую пару строка:колонка
                    return (
                      <Box>
                        {Object.entries(a).map(([rowKey, colVal], i) => (
                          <div key={i}>{rowKey}: {colVal}</div>
                        ))}
                      </Box>
                    );
                  }
                  if ((q.type === 'scale' || q.type === 'nps') && a != null) {
                    // Шкала/NPS: числовое значение
                    return String(a);
                  }
                  if (Array.isArray(a)) {
                    // Множественный выбор или другие массивы
                    return a.join(', ');
                  }
                  return String(a);
                };
                return (
                  <Box key={q.id} sx={{ mb: 2 }}>
                    <Typography variant="subtitle2">{q.text}</Typography>
                    <Typography variant="body2" color="text.secondary" component="div">{renderAnswer()}</Typography>
                  </Box>
                );
              })}
              {(selectedSurvey.interviewerQuestions || []).length > 0 && selectedResponse.interviewerAnswers && (
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid #eee' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Дополнительные вопросы интервьюера{selectedResponse.interviewerName ? ` (${selectedResponse.interviewerName})` : ''}
                  </Typography>
                  {(selectedSurvey.interviewerQuestions || []).map((iq) => {
                    const ia = selectedResponse.interviewerAnswers[iq.id];
                    return (
                      <Box key={iq.id} sx={{ mb: 2 }}>
                        <Typography variant="subtitle2">{iq.text}{iq.archived ? ' (архив)' : ''} <Typography component="span" variant="caption" color="text.secondary">({describeQuestionTargets(iq, selectedSurvey.interviewers)})</Typography></Typography>
                        <Typography variant="body2" color="text.secondary" component="div">
                          {ia === undefined || ia === null || ia === '' ? <em>Нет ответа</em> : (Array.isArray(ia) ? ia.join(', ') : String(ia))}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              )}
              {selectedResponse.flags?.fastCompletion && (
                <Chip size="small" color="warning" label="Флаг: Быстрое прохождение" />
              )}
            </Box>
          ) : (
            <Typography>—</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResponseDetailOpen(false)} sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)'
            }
          }}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* Link copied snackbar */}
      <Snackbar
        open={linkCopied}
        autoHideDuration={3000}
        onClose={() => setLinkCopied(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setLinkCopied(false)} severity="success" sx={{ width: '100%' }}>
          Ссылка скопирована в буфер обмена!
        </Alert>
      </Snackbar>

      <InterviewersDialog
        survey={interviewersSurvey}
        open={!!interviewersSurvey}
        onClose={() => setInterviewersSurvey(null)}
      />

      {/* Survey Statistics Dialog */}
      <Dialog 
        open={statsDialogOpen} 
        onClose={closeStats}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            minHeight: '80vh',
            maxHeight: '90vh'
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: '2px solid #e0e0e0',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 700
        }}>
          Статистика опроса: {selectedSurveyForStats?.title || ''}
        </DialogTitle>
        <DialogContent sx={{ p: 3, mt: 2 }}>
          {selectedSurveyForStats && (
            <SurveyStats 
              survey={selectedSurveyForStats} 
              responses={statsResponses}
              showInterviewers
            />
          )}
        </DialogContent>
        <DialogActions sx={{ borderTop: '1px solid #e0e0e0', p: 2 }}>
          <Button 
            onClick={closeStats}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: '#fff',
              '&:hover': {
                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)'
              }
            }}
          >
            Закрыть
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

